import React, { ChangeEvent, useState } from "react";
import { makeStyles, Box, CircularProgress } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import classNames from "classnames";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    position: "relative",
    "@media (max-width: 450px)": {
      "& input": {
        fontSize: "0.875rem",
      },
    },
  },
  input: {
    width: "100%",
    marginTop: theme.spacing(1.25),
    padding: theme.spacing(1.25, 2),
    fontSize: "0.875rem", // text-sm
    borderRadius: theme.shape.borderRadius * 3,
    border: "1px solid #e5e7eb", // couleur border-gray-300
    "&:focus": {
      outline: "none",
      border: `1px solid ${theme.palette.primary.light}`,
      boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "1rem", // text-base
      padding: theme.spacing(1.5, 2.5),
    },
    "@media (max-width: 450px)": {
      fontSize: "0.675rem",
      padding: theme.spacing(0.8, 3.25),
      marginTop: theme.spacing(1),
    },
  },
  // Styles pour l'icône positionnée au début
  searchIconStart: {
    position: "absolute",
    left: theme.spacing(2),
    top: "60%",
    transform: "translateY(-50%)",
    color: theme.palette.grey[500],
    "@media (max-width: 450px)": {
      left: theme.spacing(0.75),
      fontSize: "1rem",
    },
  },
  // Styles pour l'icône positionnée à la fin
  searchIconEnd: {
    position: "absolute",
    right: theme.spacing(2),
    top: "60%",
    transform: "translateY(-50%)",
    color: theme.palette.grey[500],
    "@media (max-width: 450px)": {
      right: theme.spacing(0.75),
      fontSize: "1rem",
    },
  },
  // Style additionnel pour l'input si l'icône est au début
  inputStart: {
    paddingLeft: theme.spacing(6),
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(6),
    },
  },
  // Style additionnel pour l'input si l'icône est à la fin
  inputEnd: {
    paddingRight: theme.spacing(6),
    // On garde un padding gauche normal
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      paddingRight: theme.spacing(6),
      paddingLeft: theme.spacing(2.5),
    },
  },
  pointer: {
    cursor: "pointer",
  },
  pointerNone: {
    pointerEvents: "none",
  },

  loading: {
    position: "absolute",
    right: theme.spacing(5),
    top: "50%",
    transform: "translateY(-50%)",
    "@media (max-width: 450px)": {
      right: theme.spacing(3),
      fontSize: "1rem",
    },
  },
}));

interface ICustomSearchFieldProps {
  name: string;
  placeholder?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchClicked?: () => void;
  iconPosition?: "start" | "end";
  loading?: boolean;
  searchValue?: string;
}

const CustomSearchField = (props: ICustomSearchFieldProps) => {
  const classes = useStyles();
  const {
    name,
    placeholder,
    onChange,
    onSearchClicked,
    iconPosition = "start",
    loading,
    searchValue,
  } = props;
  const [value, setValue] = useState(searchValue);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    onChange(event);
  };

  return (
    <Box className={classes.root}>
      <SearchIcon
        className={classNames(
          iconPosition === "end"
            ? classes.searchIconEnd
            : classes.searchIconStart,
          onSearchClicked ? classes.pointer : classes.pointerNone,
        )}
        onClick={onSearchClicked}
      />
      {loading && <CircularProgress className={classes.loading} size={13} />}
      <input
        name={name}
        type="text"
        placeholder={placeholder || "Rechercher..."}
        value={value}
        onChange={handleSearchChange}
        className={classNames(
          classes.input,
          iconPosition === "end" ? classes.inputEnd : classes.inputStart,
        )}
      />
    </Box>
  );
};

export default CustomSearchField;
